import { useState } from "react";
import { Download, FileText, FileSpreadsheet, FileImage, File, Lock } from "lucide-react";
import { Highlight } from "prism-react-renderer";
import { useAssistantContext } from "~/components/assistant/AssistantContext";
import { CopyButton } from "~/components/assistant/CopyButton";
import { DetailField } from "~/components/assistant/messages/DetailField";
import { LineageTab } from "~/components/assistant/messages/LineageTab";
import {
  isProteinStructureFile,
  ProteinStructureViewer,
} from "~/components/assistant/messages/ProteinStructureViewer";
import type { TextFile, BlobFile } from "~/components/assistant/types";
import { Button } from "~/components/ui/button";
import usePrismLanguages from "~/hooks/usePrismLanguages";
import useUserPermission from "~/hooks/useUserPermission";
import { codeTheme } from "~/lib/codeTheme";
import { trackEvent } from "~/utils/monitoring";

type FileArtifact = TextFile | BlobFile;

interface FileMessageProps {
  file: FileArtifact;
  artifactType: "text" | "blob";
  debugMode?: boolean;
}

type TabType = "file" | "code" | "lineage" | "details";

interface Tab {
  id: TabType;
  label: string;
}

/** Pick an icon based on file extension */
function getFileIcon(fileName: string) {
  const parts = fileName.split(".");
  const ext = parts.length > 1 ? (parts.pop()?.toLowerCase() ?? "") : "";
  if (["csv", "tsv", "xlsx", "xls", "parquet"].includes(ext)) {
    return <FileSpreadsheet className="w-5 h-5" />;
  }
  if (["png", "jpg", "jpeg", "gif", "svg", "webp", "bmp", "tiff"].includes(ext)) {
    return <FileImage className="w-5 h-5" />;
  }
  if (["txt", "md", "json", "xml", "yaml", "yml", "log", "html", "css", "js", "py"].includes(ext)) {
    return <FileText className="w-5 h-5" />;
  }
  return <File className="w-5 h-5" />;
}

/** Get a human-readable label for file type */
function getFileTypeLabel(fileName: string): string {
  const parts = fileName.split(".");
  const ext = parts.length > 1 ? parts.pop()?.toUpperCase() : undefined;
  return ext ? `${ext} file` : "File";
}

export function FileMessage({ file, artifactType, debugMode = false }: FileMessageProps) {
  const [activeTab, setActiveTab] = useState<TabType>("file");
  const [isDownloading, setIsDownloading] = useState(false);
  const { api, conversationId, contextId } = useAssistantContext();

  const { isLoading, Prism: PrismInstance } = usePrismLanguages(["python"]);

  const canDownload = useUserPermission("create:file-browser-download");

  const file_name = file.content?.file_name ?? "file";

  const handleDownload = async () => {
    if (!conversationId || !file.artifact_id || isDownloading) return;
    setIsDownloading(true);
    try {
      const downloadUrl = await api.getArtifactDownloadUrl(conversationId, file.artifact_id);
      const res = await fetch(downloadUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file_name;
      a.click();
      URL.revokeObjectURL(url);

      trackEvent("analysis_agent_artifact_downloaded", {
        dataset_id: contextId || "Unknown",
        conversation_id: conversationId,
        artifact_id: file.artifact_id,
        artifact_type: artifactType,
      });
    } catch (error) {
      console.error("Failed to download artifact:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);

    trackEvent("analysis_agent_artifact_tab_viewed", {
      dataset_id: contextId || "Unknown",
      conversation_id: conversationId || undefined,
      artifact_id: file.artifact_id || undefined,
      artifact_type: artifactType,
      context: "dataset",
      tab,
    });
  };

  const tabs: Tab[] = [{ id: "file", label: "File" }];
  if (file?.code) {
    tabs.push({ id: "code", label: "Code" });
  }
  if (file?.artifact_id) {
    tabs.push({ id: "lineage", label: "Lineage" });
  }

  if (debugMode) {
    tabs.push({ id: "details", label: "Details" });
  }

  return (
    <div
      className="bg-white border border-gray-200 rounded-lg p-4 pt-1 w-full"
      aria-label="File artifact display"
    >
      {/* Tabs */}
      <div
        className="flex border-b border-gray-200 mb-4"
        role="tablist"
        aria-label="File artifact tabs"
      >
        {tabs.map((tab) => (
          <button
            key={tab.id}
            role="tab"
            aria-selected={activeTab === tab.id}
            aria-controls={`tabpanel-${tab.id}`}
            onClick={() => handleTabChange(tab.id)}
            className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors cursor-pointer ${
              activeTab === tab.id
                ? "border-gray-900 text-gray-900"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="overflow-x-auto">
        {activeTab === "file" && (
          <div role="tabpanel" id="tabpanel-file" aria-label="File content">
            {/* File card */}
            <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 px-4 py-3">
              <div className="flex items-center justify-center w-9 h-9 rounded-md bg-white border border-gray-200 text-gray-500 flex-shrink-0">
                {getFileIcon(file_name)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{file_name}</p>
                <p className="text-xs text-gray-500">{getFileTypeLabel(file_name)}</p>
              </div>
              {file.artifact_id &&
                (canDownload ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownload}
                    loading={isDownloading}
                    aria-label="Download file"
                  >
                    <Download className="size-3.5" />
                    Download
                  </Button>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-gray-600">
                    <Lock className="size-3" />
                    Contact your admin to enable downloads
                  </span>
                ))}
            </div>

            {file.artifact_id && isProteinStructureFile(file_name) && (
              <div className="mt-3">
                <ProteinStructureViewer artifactId={file.artifact_id} fileName={file_name} />
              </div>
            )}

            {file?.summary && <div className="mt-3 text-xs text-gray-600">{file.summary}</div>}
          </div>
        )}

        {activeTab === "code" && file?.code && (
          <div role="tabpanel" id="tabpanel-code" aria-label="Code content">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-medium text-gray-700">Python Code</h4>
              <CopyButton text={file.code} variant="light" size="sm" />
            </div>
            <div className="h-full overflow-hidden rounded-sm">
              {!isLoading && (
                <Highlight
                  theme={codeTheme}
                  code={file.code}
                  language="python"
                  prism={PrismInstance as any}
                >
                  {({ style, tokens, getLineProps, getTokenProps, className }) => (
                    <pre
                      className={`not-prose h-full p-4 overflow-x-auto text-xs font-mono leading-relaxed ${
                        className || ""
                      }`}
                      style={style}
                    >
                      {tokens.map((line, i) => (
                        <div
                          {...getLineProps({ line, key: i })}
                          key={`line-${i}`}
                          style={{ minHeight: "1.5em" }}
                        >
                          {line.map((token, key) => (
                            <span {...getTokenProps({ token, key })} key={`token-${key}`} />
                          ))}
                        </div>
                      ))}
                    </pre>
                  )}
                </Highlight>
              )}
              {isLoading && (
                <div className="h-full p-4 bg-gray-800 text-gray-300 text-sm font-mono leading-relaxed">
                  Loading syntax highlighting...
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "lineage" && file?.artifact_id && (
          <div role="tabpanel" id="tabpanel-lineage" aria-label="Lineage content">
            <LineageTab artifactId={file.artifact_id} />
          </div>
        )}

        {activeTab === "details" && debugMode && (
          <div
            role="tabpanel"
            id="tabpanel-details"
            aria-label="Details content"
            className="space-y-3 pt-2 pb-4 px-4 rounded-lg border border-gray-200 text-gray-800"
          >
            <DetailField label="File Data" content={file} />
          </div>
        )}
      </div>
    </div>
  );
}
